<div class="header">
  <div>
    <h2>{{ 'academy.title' | translate }}</h2>
    <p class="subtitle">{{ 'academy.subtitle' | translate }}</p>
  </div>
</div>

<app-page-loader *ngIf="!ready"></app-page-loader>

<ng-container *ngIf="ready">
  <!-- Begrüßung + Weiterlernen: zwei Kacheln in einer Reihe, gleiche Höhe. -->
  <section class="academy-hero" alAnchor="academy.continue">
    <div class="welcome al-glass al-glass--tile">
      <div class="al-glass__body">
        <h3 class="welcome__title">
          {{ (name ? 'academy.home.welcomeName' : 'academy.home.welcome') | translate: { name: name } }}
        </h3>
        <p class="welcome__text">
          {{
            (overall.completed === 0
              ? 'academy.home.statusFresh'
              : overall.completed === overall.total
                ? 'academy.home.statusDone'
                : 'academy.home.status'
            ) | translate: { done: overall.completed, total: overall.total }
          }}
        </p>
        <app-academy-progress
          class="is-large"
          [done]="overall.completed"
          [total]="overall.total"
          [label]="'academy.home.progress' | translate"
        ></app-academy-progress>
        <dl class="welcome__stats">
          <div>
            <dt>{{ 'academy.status.completed' | translate }}</dt>
            <dd>{{ overall.completed }}</dd>
          </div>
          <div>
            <dt>{{ 'academy.status.started' | translate }}</dt>
            <dd>{{ overall.started }}</dd>
          </div>
          <div>
            <dt>{{ 'academy.status.not_started' | translate }}</dt>
            <dd>{{ overall.open }}</dd>
          </div>
        </dl>
      </div>
    </div>

    <div class="continue al-glass al-glass--tile">
      <div class="al-glass__body">
        <div class="continue__orbi" aria-hidden="true">
          <app-orbi-lab type="tutor" [size]="64" [script]="orbiScript"></app-orbi-lab>
        </div>
        <ng-container *ngIf="next; else allDone">
          <span class="continue__eyebrow">
            {{ (next.status === 'started' ? 'academy.home.continueStarted' : 'academy.home.continueNext') | translate }}
          </span>
          <h3 class="continue__title">{{ next.title }}</h3>
          <p class="continue__summary">{{ nextSummary }}</p>
          <a nbButton status="primary" class="continue__action" [routerLink]="['/academy/lessons', next.lesson.id]">
            {{ (next.status === 'started' ? 'academy.home.resume' : 'academy.home.start') | translate }}
            <nb-icon icon="arrow-forward-outline"></nb-icon>
          </a>
        </ng-container>
        <ng-template #allDone>
          <span class="continue__eyebrow">{{ 'academy.home.allDoneEyebrow' | translate }}</span>
          <h3 class="continue__title">{{ 'academy.home.allDoneTitle' | translate }}</h3>
          <p class="continue__summary">{{ 'academy.home.allDoneText' | translate }}</p>
        </ng-template>
      </div>
    </div>
  </section>

  <!-- Suche + Filter: Pillen wie im ganzen Portal. -->
  <div class="academy-toolbar">
    <app-search-input
      class="academy-toolbar__search"
      alAnchor="academy.search"
      placeholderKey="academy.searchPlaceholder"
      [value]="query"
      (valueChange)="onQuery($event)"
    ></app-search-input>
    <app-segmented-control
      alAnchor="academy.filter"
      [options]="filterOptions"
      [value]="filter"
      [ariaLabel]="'academy.filter.label' | translate"
      (valueChange)="onFilter($event)"
    ></app-segmented-control>
  </div>

  <!-- Suchergebnis / gefilterte Liste -->
  <nb-card *ngIf="searching" class="academy-results">
    <nb-card-header>
      <h6>
        {{ (filter === 'hints' ? 'academy.home.hintsTitle' : 'academy.home.resultsTitle') | translate }}
        <span class="academy-results__count">{{ results.length }}</span>
      </h6>
    </nb-card-header>
    <nb-card-body>
      <p class="academy-results__hint" *ngIf="filter === 'hints'">{{ 'academy.home.hintsText' | translate }}</p>
      <nb-list *ngIf="results.length; else noResults">
        <nb-list-item *ngFor="let r of results; trackBy: trackRow">
          <app-academy-lesson-item
            [lesson]="r.lesson"
            [title]="r.title"
            [subtitle]="r.subtitle"
            [status]="r.status"
            [isNew]="r.isNew"
          ></app-academy-lesson-item>
        </nb-list-item>
      </nb-list>
      <ng-template #noResults>
        <div class="academy-empty">
          <p>{{ 'academy.home.noResults' | translate }}</p>
          <button nbButton appearance="outline" status="primary" size="small" type="button" (click)="resetSearch()">
            {{ 'academy.home.resetSearch' | translate }}
          </button>
        </div>
      </ng-template>
    </nb-card-body>
  </nb-card>

  <ng-container *ngIf="!searching">
    <!-- Lernpfad: die Themen bauen aufeinander auf — Kacheln im Look des
         LinkedIn-Carousels (Figma „LinkedIn-Assets“, Alphabees_Carousel):
         Verlauf-Hintergrund, dünnes weißes Raster, große Nummer oben rechts,
         Milchglas-Feld mit dem Inhalt. Einzelne Kacheln, kein Carousel. -->
    <div class="academy-section-title">
      <h3>{{ 'academy.home.path' | translate }}</h3>
    </div>
    <p class="path-intro page-hint">{{ 'academy.home.pathIntro' | translate }}</p>

    <div class="path" alAnchor="academy.topics">
      <ol class="path-grid">
        <li
          *ngFor="let t of topics; trackBy: trackTopic"
          class="path-slide"
          [class.is-done]="t.state === 'done'"
          [class.is-current]="t.state === 'current'"
        >
          <a class="path-slide__link" [routerLink]="['/academy/topics', t.topic.id]">
            <span class="path-slide__number" aria-hidden="true">{{ t.number }}</span>
            <span class="path-slide__panel">
              <span class="path-slide__eyebrow">
                {{ 'academy.home.pathStep' | translate: { n: t.number } }}
                <span class="path-slide__state" *ngIf="t.state === 'current'">· {{ 'academy.home.pathCurrent' | translate }}</span>
                <span
                  class="path-slide__state"
                  *ngIf="t.state === 'done'"
                  [attr.aria-label]="'academy.status.completed' | translate"
                >· <nb-icon icon="checkmark-outline"></nb-icon></span>
              </span>
              <span class="path-slide__title">{{ t.title }}</span>
              <span class="path-slide__divider" aria-hidden="true"></span>
              <span class="path-slide__text">{{ t.description }}</span>
              <span class="path-slide__progress">
                {{ 'academy.progress.of' | translate: { done: t.completed, total: t.total } }}
              </span>
              <span class="path-slide__cta">
                <span class="path-slide__ticks" aria-hidden="true">
                  <span *ngFor="let done of t.ticks" class="path-slide__tick" [class.is-done]="done"></span>
                </span>
                {{
                  (t.state === 'done'
                    ? 'academy.home.pathReview'
                    : t.completed > 0
                      ? 'academy.home.pathContinue'
                      : 'academy.home.pathStart'
                  ) | translate
                }}
              </span>
            </span>
          </a>
        </li>
      </ol>
    </div>

    <!-- Neuigkeiten + Zertifikate: zwei Karten, gleiche Höhe (Regel 3). -->
    <div class="academy-columns">
      <nb-card class="academy-news">
        <nb-card-header>
          <h6>{{ 'academy.home.news' | translate }}</h6>
        </nb-card-header>
        <nb-card-body>
          <nb-list>
            <nb-list-item *ngFor="let n of news">
              <div class="news-item">
                <span class="news-item__date">{{ n.news.date | date: 'dd.MM.yyyy' }}</span>
                <span class="news-item__title">{{ n.title }}</span>
                <span class="news-item__text">{{ n.text }}</span>
                <a *ngIf="n.lesson" class="news-item__link" [routerLink]="['/academy/lessons', n.lesson.id]">
                  {{ 'academy.home.toLesson' | translate }}
                  <nb-icon icon="arrow-forward-outline"></nb-icon>
                </a>
              </div>
            </nb-list-item>
          </nb-list>
        </nb-card-body>
      </nb-card>

      <nb-card class="academy-certs" alAnchor="academy.certificates">
        <nb-card-header>
          <h6>{{ 'academy.home.certificates' | translate }}</h6>
        </nb-card-header>
        <nb-card-body>
          <nb-list>
            <nb-list-item *ngFor="let c of certs">
              <a class="cert-item" [routerLink]="['/academy/certificates', c.state.cert.id]">
                <span class="cert-item__badge" [class.is-earned]="c.state.earned" aria-hidden="true">
                  <nb-icon [icon]="c.state.earned ? 'award' : 'award-outline'"></nb-icon>
                </span>
                <span class="cert-item__text">
                  <span class="cert-item__title">{{ c.title }}</span>
                  <app-academy-progress
                    *ngIf="!c.state.earned"
                    [done]="c.state.completed"
                    [total]="c.state.lessons.length"
                  ></app-academy-progress>
                  <span class="cert-item__note" *ngIf="c.state.earned">
                    {{ 'academy.cert.earnedScore' | translate: { score: c.state.bestScore } }}
                  </span>
                </span>
                <span class="cert-item__status">
                  <span class="academy-chip academy-chip--success" *ngIf="c.state.earned">
                    {{ 'academy.cert.earned' | translate }}
                  </span>
                  <span class="academy-chip" *ngIf="!c.state.earned && c.state.examUnlocked">
                    {{ 'academy.cert.examReady' | translate }}
                  </span>
                  <span class="academy-chip academy-chip--muted" *ngIf="!c.state.earned && !c.state.examUnlocked && c.state.missingPrerequisite">
                    {{ 'academy.cert.needs' | translate: { name: c.missingTitle } }}
                  </span>
                  <span class="academy-chip academy-chip--muted" *ngIf="!c.state.earned && !c.state.examUnlocked && !c.state.missingPrerequisite">
                    {{ 'academy.cert.lessonsLeft' | translate: { n: c.state.lessons.length - c.state.completed } }}
                  </span>
                </span>
              </a>
            </nb-list-item>
          </nb-list>
        </nb-card-body>
      </nb-card>
    </div>
  </ng-container>
</ng-container>
